import type { Bridge, HostInfo } from '@casa/bridge';
import { appLog, captureConsole } from './log.ts';
import { readLogSinkSettings, startLogSink, writeLogSinkSettings, type LogSink, type LogSinkSettings } from './log_sink.ts';

// Starts the page's diagnostics: the console goes into the log, and the remote sink runs if the
// user turned it on. The settings page changes the sink through `saveLogSinkSettings`.

let sink: LogSink | undefined;

async function deviceOf(bridge: Bridge) : Promise<Record<string, string>> {
    const host = (globalThis as { casaHost?: HostInfo }).casaHost;
    const device: Record<string, string> = { shell: host?.shell ?? 'browser', shellVersion: host?.shellVersion ?? '' };
    try {
        const info = await bridge.device.info();
        Object.assign(device, { platform: info.platform, osVersion: info.osVersion, model: info.model });
    } catch {
        // A host without device.info: the batches say less about where they come from.
    }

    return device;
}

async function applyLogSink(bridge: Bridge, settings: LogSinkSettings) : Promise<void> {
    sink?.stop();
    sink = settings.enabled && settings.url !== '' ? startLogSink({ log: appLog, bridge, url: settings.url, device: await deviceOf(bridge) }) : undefined;
}

export async function startDiagnostics(bridge: Bridge | null) : Promise<void> {
    captureConsole(appLog, console);
    if (bridge) {
        await applyLogSink(bridge, await readLogSinkSettings(bridge));
    }
}

/** Stores the sink's settings and applies them at once. */
export async function saveLogSinkSettings(bridge: Bridge, settings: LogSinkSettings) : Promise<void> {
    await writeLogSinkSettings(bridge, settings);
    await applyLogSink(bridge, settings);
}

/** Sends what the sink has not sent yet; rejects with the reason when it fails, or when the sink is off. */
export async function flushLogSink() : Promise<void> {
    if (!sink) {
        throw new Error('the log is not being sent');
    }

    await sink.flush();
}
